import { TrackModel } from '@customer/store/apis/investigationsApi';
import { ReactElement, useMemo } from 'react';
import { LayersControl, FeatureGroup } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import {
  InvestigationEnitityIdProps,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { getMostRecentPoint } from '../tracks-markers-group/TracksMarkersGroup';
import { BeamsLayer, BeamsLayerProps } from '../beams-layer/BeamsLayer';

const getBeamSettings = (
  tracks: TrackModel[],
  assetId: number,
  time: number,
): BeamsLayerProps | undefined => {
  const assetTrack: TrackModel | undefined = tracks.find(({ asset }) => asset === assetId);

  // Exit early if there was no matching asset track or the asset track has no points.
  if (!assetTrack?.points) return undefined;

  const {
    points: { features },
    yrot,
    wid,
    len,
  } = assetTrack;

  const mostRecentPoint = getMostRecentPoint(features, time);

  // Exit early if there was no matching point.
  if (!mostRecentPoint) return undefined;

  // Get the position and heading from the most recent point.
  const {
    geometry: {
      coordinates: [X, Y],
    },
    properties: { hdg: heading, beamdata },
  } = mostRecentPoint;

  if (beamdata) {
    return {
      position: [Y, X],
      heading,
      beamData: beamdata,
      bodyWidth: wid,
      bodyLength: len,
      yRotOfs: yrot,
    };
  }

  return undefined;
};

interface BeamsLayerGroupProps extends InvestigationEnitityIdProps {
  tracks: TrackModel[];
  checked?: boolean;
}

export const BeamsLayerGroup = ({
  siteId,
  investigationId,
  tracks = [],
  checked = true,
}: BeamsLayerGroupProps): ReactElement | null => {
  const { t } = useTranslation();

  const { assetControls = {}, playback } = useInvestigationEntity({
    siteId,
    investigationId,
  });

  const visibleAssetIds = useMemo<number[]>(() => {
    return Object.entries(assetControls)
      .filter(([_, { visible }]) => visible)
      .map(([assetId]) => Number(assetId));
  }, [assetControls]);

  const time: number | undefined = playback?.currentTime;

  if (typeof time === 'undefined') return null;

  return (
    <LayersControl.Overlay name={t('Field.Beam.Label_other')} checked={checked}>
      <FeatureGroup key="beams">
        {visibleAssetIds.map(assetId => {
          const settings = getBeamSettings(tracks, assetId, time);

          if (settings) {
            const { position, heading, beamData, bodyLength, bodyWidth, yRotOfs } = settings;

            return (
              <BeamsLayer
                key={assetId}
                position={position}
                heading={heading}
                beamData={beamData}
                bodyWidth={bodyWidth}
                bodyLength={bodyLength}
                yRotOfs={yRotOfs}
              />
            );
          }

          return null;
        })}
      </FeatureGroup>
    </LayersControl.Overlay>
  );
};
